/**
 * Celebration confetti — lazy-loads canvas-confetti (separate chunk, so WeChat first paint
 * carries zero extra JS) and no-ops entirely under prefers-reduced-motion. Money-restraint
 * rule: never call this near amount entry or the 最终价 confirm panel.
 *
 * Renders on our OWN canvas with useWorker:false — the library's default worker/OffscreenCanvas
 * path crashes some WebViews (and headless Chromium), and an explicit canvas lets the
 * html.is-capturing screenshot freeze hide it deterministically via .confetti-canvas.
 */
type ConfettiFn = (opts: Record<string, unknown>) => Promise<unknown> | null;
type ConfettiModule = { default: ConfettiFn & { create(canvas: HTMLCanvasElement, opts: Record<string, unknown>): ConfettiFn } };

let firePromise: Promise<ConfettiFn> | null = null;

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function getFire(): Promise<ConfettiFn> {
  firePromise ??= (import('canvas-confetti') as unknown as Promise<ConfettiModule>).then((m) => {
    const canvas = document.createElement('canvas');
    canvas.className = 'confetti-canvas';
    Object.assign(canvas.style, { position: 'fixed', inset: '0', width: '100%', height: '100%', pointerEvents: 'none', zIndex: '3000' });
    document.body.appendChild(canvas);
    return m.default.create(canvas, { resize: true, useWorker: false });
  });
  return firePromise;
}

export async function fireConfetti(preset: 'submit' | 'award' = 'submit') {
  if (reduced()) return;
  try {
    const confetti = await getFire();
    const common = { disableForReducedMotion: true } as const;
    if (preset === 'award') {
      void confetti({ ...common, particleCount: 130, spread: 100, origin: { y: 0.55 } });
      window.setTimeout(() => void confetti({ ...common, particleCount: 60, spread: 70, origin: { x: 0.2, y: 0.6 } }), 250);
      window.setTimeout(() => void confetti({ ...common, particleCount: 60, spread: 70, origin: { x: 0.8, y: 0.6 } }), 450);
    } else {
      void confetti({ ...common, particleCount: 70, spread: 65, origin: { y: 0.7 } });
    }
  } catch (e) {
    console.warn('confetti unavailable — celebration skipped', e);
  }
}

/** Once-per-key celebration (e.g. award confetti must not re-fire on every dashboard visit). */
export function celebrateOnce(key: string, preset: 'submit' | 'award' = 'award') {
  try {
    const k = `celebrated_${key}`;
    if (localStorage.getItem(k)) return;
    localStorage.setItem(k, '1');
  } catch { /* private mode — celebrate anyway */ }
  void fireConfetti(preset);
}
